import {
  APIFieldAttributes,
  APIFieldType,
  APIModelAttributes,
  ArdoqId,
  APIFieldGlobalAttributes,
} from '@ardoq/api-types';
import { workspaceInterface } from '@ardoq/workspace-interface';
import type {
  GetFieldDataForFiltersAndFormatting,
  ExistsByName,
  GetFieldsCount,
  GetFieldAttributes,
  GetAllFieldsOfWorkspace,
  GetFieldsOfWorkspace,
  GetAllowedValues,
  Format,
  GetLabel,
  AppliesToComponent,
  GetByName,
  GetGlobalFieldByName,
  GetNumberFormatByName,
  IsExternallyManagedForComponentsWithinWorkspace,
  IsExternallyManagedForReferencesWithinWorkspace,
  GetFieldsByComponentType,
  GetFieldsByReferenceType,
  FieldInterfaceImplementation,
  GetAllFields,
  GetFieldTypeFromFieldName,
} from '@ardoq/field-interface';
import {
  dateRangeOperations,
  getDateRangeType,
  getStartDateName,
} from '@ardoq/date-range';
import { referenceInterface } from '@ardoq/reference-interface';
import { componentInterface } from '@ardoq/component-interface';
import { fieldUtils } from '@ardoq/scope-data';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { usersOperations } from '@ardoq/core';
import { fieldOps } from 'models/utils/fieldOps';
import { fields$, toFieldTypeKey } from 'streams/fields/fields$';
import { globalFields$ } from 'streams/fields/globalFields$';

const get: GetFieldAttributes = id => fields$.state.byId[id];

const getAllFields: GetAllFields = () => fields$.state.models;

const getAllFieldsOfWorkspace: GetAllFieldsOfWorkspace = workspaceId => {
  const modelId = workspaceInterface.getWorkspaceModelId(workspaceId);
  if (!modelId) return [];
  return getAllFields().filter(field => field.model === modelId);
};

const getFieldsOfWorkspace: GetFieldsOfWorkspace = (workspaceId, fieldTypes) =>
  getAllFieldsOfWorkspace(workspaceId).filter(field =>
    fieldTypes.includes(field.type)
  );

const getAllowedValues: GetAllowedValues = fieldId => {
  const field = get(fieldId);
  return field ? fieldOps.getAllowedValues(field) : [];
};

const format: Format = ({
  fieldId,
  value,
  fieldType,
  excludeTime,
  textAreaFormat,
}) => {
  // For date range fields, is better to pass the fieldType explicitly when possible. Because if you look up the
  // date range field id in the fields collection, it will return the start date field, which is a date time field.
  const fieldAttributes = get(fieldId);
  if (!fieldAttributes) return null;

  // i18nLocale$.state / orgUsers$.state OK here: imperative fieldInterface.format invoked
  // from event handlers, AG Grid value formatters and other one-shot callers.
  return fieldUtils.fieldAttributesToLabel({
    fieldAttributes,
    fieldType,
    value,
    excludeTime,
    textAreaFormat,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsers$.state.byId, val),
    i18nLocale: i18nLocale$.state,
  });
};

const getLabel: GetLabel = fieldId => {
  const field = get(fieldId);
  return field ? fieldOps.getRawLabel(field) : null;
};

const appliesToComponent: AppliesToComponent = (fieldId, componentId) => {
  const field = get(fieldId);
  const component = componentInterface.get(componentId);
  if (!field || !component) return false;

  const { typeId, model } = component;
  return Boolean(
    typeId && field.model === model && field.componentType.includes(typeId)
  );
};

const getFieldByName = (fieldName: string): APIFieldAttributes | null => {
  if (!fieldName) return null;
  return fields$.state.byName.get(fieldName) ?? null;
};

const isTemplateModel = (model: APIModelAttributes | null): boolean => {
  if (!model) return false;
  return Boolean(model.common || model.useAsTemplate);
};

const getFieldByNameExcludingTemplates = (
  fieldName: string
): APIFieldAttributes | null => {
  const field = getFieldByName(fieldName);
  if (!field) return null;
  const workspaceId = workspaceInterface.getWorkspaceIdByModelId(field.model);
  if (!workspaceId) return field;
  const model = workspaceInterface.getModelData(workspaceId);
  return isTemplateModel(model) ? null : field;
};

const getByName: GetByName = (fieldName, options = {}) => {
  const { includeTemplateFields = true } = options;
  return includeTemplateFields
    ? getFieldByName(fieldName)
    : getFieldByNameExcludingTemplates(fieldName);
};

const existsByName: ExistsByName = fieldName =>
  getFieldByName(fieldName) !== null;

const getGlobalFieldByName: GetGlobalFieldByName = fieldName => {
  if (!fieldName) return null;
  // globalFields$ replaces date range constituent fields with a single merged entry
  // (e.g. 'deranged_start_date' and 'deranged_end_date' → 'deranged_start_date,deranged_end_date'),
  // so lookups by the individual part name always miss. Fall back to fields$, which keeps
  // constituent fields as separate entries under their original names.
  return (
    globalFields$.state.byName[fieldName] ??
    (dateRangeOperations.fieldNameIsPartOfDateRangeField(fieldName)
      ? getFieldByNameExcludingTemplates(fieldName)
      : null)
  );
};

const getNumberFormatByName: GetNumberFormatByName = fieldName => {
  const field = getFieldByNameExcludingTemplates(fieldName);

  if (field === null) return null;
  if (field.type !== APIFieldType.NUMBER) return null;

  return field.numberFormatOptions ?? null;
};

const isExternallyManagedForComponentsWithinWorkspace: IsExternallyManagedForComponentsWithinWorkspace =
  (fieldName, workspaceId) =>
    workspaceInterface
      .getWorkspaceIntegrations(workspaceId)
      .flatMap(integration => integration.componentFields)
      .includes(fieldName);

const isExternallyManagedForReferencesWithinWorkspace: IsExternallyManagedForReferencesWithinWorkspace =
  (fieldName, workspaceId) =>
    workspaceInterface
      .getWorkspaceIntegrations(workspaceId)
      .flatMap(integration => integration.referenceFields)
      .includes(fieldName);

const getFieldsByComponentType: GetFieldsByComponentType = (
  componentTypeId: string,
  modelId: string
) =>
  fields$.state.byComponentType.get(toFieldTypeKey(modelId, componentTypeId)) ??
  [];

const getFieldsByReferenceType: GetFieldsByReferenceType = (
  referenceTypeId,
  modelId
) =>
  fields$.state.byReferenceType.get(toFieldTypeKey(modelId, referenceTypeId)) ??
  [];

const getFieldsCount: GetFieldsCount = () => getAllFields().length;

const getFieldDataForFiltersAndFormatting: GetFieldDataForFiltersAndFormatting =
  (workspaceIds: ArdoqId[]) => {
    const modelIds = workspaceIds.map(workspaceInterface.getWorkspaceModelId);
    const modelIdsSet = new Set(modelIds);
    const relatedFieldsByName: Record<string, APIFieldGlobalAttributes> = {};
    const componentFields: APIFieldGlobalAttributes[] = [];
    const referenceFields: APIFieldGlobalAttributes[] = [];

    getAllFields().forEach(attributes => {
      const { model, name } = attributes;
      const belongsToRelatedWorkspace = modelIdsSet.has(model);

      if (relatedFieldsByName[model] && !belongsToRelatedWorkspace) {
        return;
      }

      const field = pickGlobalFieldAttributes(attributes);
      relatedFieldsByName[name] = field;

      if (belongsToRelatedWorkspace) {
        if (isComponentField(attributes)) {
          componentFields.push(field);
        }

        if (isReferenceField(attributes)) {
          referenceFields.push(field);
        }
      }
    });

    return {
      relatedFieldsByName,
      componentFields,
      referenceFields,
    };
  };

const isComponentField = ({ componentType }: APIFieldAttributes) =>
  Boolean(componentType.length > 0);

const isReferenceField = ({ referenceType }: APIFieldAttributes) =>
  Boolean(referenceType.length > 0);

const pickGlobalFieldAttributes = ({
  calculatedFieldSettings,
  defaultValue,
  description,
  label,
  name,
  type,
  numberFormatOptions,
  model,
}: APIFieldAttributes): APIFieldGlobalAttributes => ({
  calculatedFieldSettings,
  defaultValue,
  description,
  label,
  name,
  type,
  numberFormatOptions,
  model,
});

const getFieldValueForComponentOrReference = (
  modelId: ArdoqId,
  fieldName: string
) =>
  referenceInterface.isReference(modelId)
    ? (referenceInterface.get(modelId)?.[fieldName] ?? null)
    : (componentInterface.get(modelId)?.[fieldName] ?? null);

const formatFieldValueForComponentOrReference = (
  fieldId: ArdoqId,
  modelId: ArdoqId
) => {
  const fieldAttributes = get(fieldId);
  if (!fieldAttributes?.name) return null;

  const fieldValue = getFieldValueForComponentOrReference(
    modelId,
    fieldAttributes.name
  );

  // i18nLocale$.state / orgUsers$.state OK here: imperative
  // fieldInterface.formatFieldValueForComponentOrReference invoked from
  // labels and event handlers without reactive context.
  return fieldUtils.fieldAttributesToLabel({
    fieldAttributes,
    value: fieldValue,
    excludeTime: true,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsers$.state.byId, val),
    textAreaFormat: 'text',
    i18nLocale: i18nLocale$.state,
  });
};

const getFieldTypeFromFieldName: GetFieldTypeFromFieldName = fieldName => {
  if (!fieldName) return null;

  if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
    const field = getFieldByName(getStartDateName(fieldName));
    return field ? getDateRangeType(field.type) : null;
  }

  const field = getFieldByName(fieldName);
  return field ? field.type : null;
};

/**
 * `fieldInterface` is a Backbone-era singleton: every member reads the field
 * collection -- and for formatting, the current locale and org users -- from
 * global mutable state at call time.
 *
 * The app is stream-based now. Resolve fields from `fields$`
 * (`streams/fields/fields$.ts`) or `globalFields$` at a permitted boundary -- a
 * `*$.ts` file, a file inside a `*$/` folder, or a `*routines.ts` -- and thread
 * the resolved data downstream as plain parameters.
 *
 * The pure counterparts are `fieldOperations` (`@ardoq/core`) for the type
 * predicates and value reads -- `getFieldValue`, `isDateOnlyField`,
 * `isDateTimeField`, `isNumberField`, `isListField`, `isURLField`,
 * `getListFieldOptions`, `appliesToComponentType`, `appliesToReferenceType` --
 * and `fieldUtils` (`@ardoq/scope-data`) for label and value formatting
 * (`fieldAttributesToLabel`, `formatFieldValueForComponentOrReference`,
 * `getFieldValueForComponentOrReference`, `getAllFieldsInWorkspace`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const fieldInterface: FieldInterfaceImplementation = {
  /**
   * @deprecated Call `fieldUtils.getAllFieldsInWorkspace` (`@ardoq/scope-data`), or read `fieldsByWorkspaceId$` (`streams/fields/fieldsByWorkspaceId$.ts`).
   */
  getAllFieldsOfWorkspace,
  /**
   * @deprecated Call `fieldUtils.getAllFieldsInWorkspace` (`@ardoq/scope-data`), or read `fieldsByWorkspaceId$` (`streams/fields/fieldsByWorkspaceId$.ts`).
   */
  getFieldsOfWorkspace,
  /**
   * @deprecated Call `fieldOperations.getListFieldOptions` (`@ardoq/core`) with the field resolved from `fields$`.
   */
  getAllowedValues,
  /**
   * @deprecated Call the pure `fieldUtils.fieldAttributesToLabel` (`@ardoq/scope-data`) with a field from `fields$`; this member snapshots `i18nLocale$` and `orgUsers$` internally.
   */
  format,
  /**
   * @deprecated Read `fields$.byId[id]?.label`, or call `fieldUtils.getLabel` / `fieldUtils.getLabelByFieldName` (`@ardoq/scope-data`).
   */
  getLabel,
  /**
   * @deprecated Call `fieldOperations.appliesToComponentType` (`@ardoq/core`) or `fieldUtils.appliesToComponent` (`@ardoq/scope-data`).
   */
  appliesToComponent,
  /**
   * @deprecated Read `fields$.byName`.
   */
  getByName,
  /**
   * @deprecated Read `fields$.byName`.
   */
  existsByName,
  /**
   * @deprecated Read `globalFields$.byName` (`streams/fields/globalFields$.ts`).
   */
  getGlobalFieldByName,
  /**
   * @deprecated Read `fields$.byName` and narrow with `fieldOperations.isNumberField` (`@ardoq/core`).
   */
  getNumberFormatByName,
  /**
   * @deprecated Call `componentUtils.isFieldExternallyManaged(scopeData, componentId, fieldName)` (`@ardoq/scope-data`), or check the workspace integrations from `workspaceUtils.getWorkspaceIntegrations`.
   */
  isExternallyManagedForComponentsWithinWorkspace,
  /**
   * @deprecated Check the workspace integrations from `workspaceUtils.getWorkspaceIntegrations` (`@ardoq/scope-data`) against the reference field names.
   */
  isExternallyManagedForReferencesWithinWorkspace,
  /**
   * @deprecated Read `fields$.byComponentType`, or call `fieldUtils.getFieldsByComponentType` (`@ardoq/scope-data`).
   */
  getFieldsByComponentType,
  /**
   * @deprecated Read `fields$.byReferenceType`, or call `fieldUtils.getFieldsByReferenceType` (`@ardoq/scope-data`).
   */
  getFieldsByReferenceType,
  /**
   * @deprecated Read `fields$.models`.
   */
  getAllFields,
  /**
   * @deprecated Read `fields$.models.length`.
   */
  getFieldsCount,
  /**
   * @deprecated Call `fieldUtils.getFieldData` (`@ardoq/scope-data`) with fields from `fields$`.
   */
  getFieldDataForFiltersAndFormatting,
  /**
   * @deprecated Join `globalFields$` and pick from the resolved field with `fieldOperations` (`@ardoq/core`).
   */
  pickGlobalFieldAttributes,
  /**
   * @deprecated Read `fields$.byId[id]`.
   */
  get,
  /**
   * @deprecated Call `fieldUtils.getFieldValueForComponentOrReference` (`@ardoq/scope-data`), or `fieldOperations.getFieldValue` (`@ardoq/core`).
   */
  getFieldValueForComponentOrReference,
  /**
   * @deprecated Call `fieldUtils.formatFieldValueForComponentOrReference` (`@ardoq/scope-data`) with fields from `fields$`, the locale from `i18nLocale$` and users from `orgUsers$`.
   */
  formatFieldValueForComponentOrReference,
  /**
   * @deprecated Read `fields$.byName` and narrow with the `fieldOperations` predicates -- `isDateOnlyField`, `isDateTimeField`, `isNumberField`, `isListField`, `isURLField` (`@ardoq/core`).
   */
  getFieldTypeFromFieldName,
};
